/* <lt-frame-preview> — the frame preview over the front shot's gallery slide
   (snippets/lt-frame-preview.liquid, look: assets/lt-frame-preview.css).

   The selection is read from the picker's checked radios, not from the
   server: the frame radios carry their key and material
   (snippets/product-frame-options.liquid) and the picker's
   `[data-frame-state]` script names the three radio groups and says whether
   frames are sold in this country (snippets/product-variant-picker.liquid).
   So the preview redraws on the click; `variantChange` (after
   product-info-bento.js swapped in the server's picker) only resyncs.

   Every length is real-world inches times one scale, written as cqh of the
   stage, so the piece grows with the size and the CSS transitions do the
   animation. The layers are the frame generator's renders as 9-slice
   border-images; the script only switches which are on (`is-on`) and sets
   their widths. */
(function () {
  if (customElements.get('lt-frame-preview')) return;

  /* Frame sizes in inches by size handle (cm labels map to the inch frame
     Artelo makes), and the passe-partout window of each frame
     (docs/ramy-i-passe-partout-plan.md, "Rozmiary"). Any other label (a
     square "30x30 cm" one day) is drawn from its own numbers, without a
     passe-partout: there is no window for it here. */
  var CM_TO_IN = { '30x40': '12x16', '45x60': '18x24', '50x70': '20x28', '60x90': '24x36' };
  var MAT_WINDOW = { '12x16': [8.5, 11], '18x24': [16, 20], '20x28': [16, 24], '24x36': [20, 30] };
  var IN = 2.54;

  /* Window inset in inches, from the generator's geometry.json: wood
     moulding 0.55", canvas moulding 0.55" + gap 0.47". */
  var WINDOW_IN = { wood: 0.55, canvas: 1.02, none: 0 };

  /* The renders in assets/ as inches: each slice in lt-frame-preview.css
     (pixels of the file) over the file's pixels per inch (generator ppi of
     the 18x24 layer times the export scale). Order: top, right, bottom,
     left. A border-image width set to these keeps the render at its real
     size on every frame size. */
  var PPI = { frameWood: 52.37, frameCanvas: 49.9, sheet: 55.54 };
  var SLICE_IN = {
    frameWood: [38 / PPI.frameWood],
    frameCanvas: [54 / PPI.frameCanvas],
    shadowWood: [2, 20, 26, 7].map(function (px) { return px / PPI.frameWood; }),
    shadowCanvas: [2, 20, 25, 8].map(function (px) { return px / PPI.frameCanvas; }),
    paper: [4, 22, 28, 9].map(function (px) { return px / PPI.sheet; }),
    mat: [118, 60, 115, 62].map(function (px) { return px / PPI.sheet; }),
    // lt-mat-landscape.webp: the same render transposed (lt-frame-preview.css).
    matLandscape: [62, 115, 60, 118].map(function (px) { return px / PPI.sheet; })
  };
  // Passe-partout margins of the 18x24 render (x, y): 1" and 2"; transposed
  // for the landscape render.
  var MAT_REF_IN = { portrait: [1, 2], landscape: [2, 1] };
  // Canvas weave tile: 64 px at the generator's 67.76 ppi.
  var WEAVE_IN = 64 / 67.76;

  /* Height of the piece on the stage, in % of the stage height, for the
     smallest and the largest outer height: physical size, compressed so a
     30x40 is not lost and a 60x90 still has wall around it. */
  var SCALE = { minCm: 44, maxCm: 95, minPct: 46, maxPct: 82, widthPct: 86 };

  function parseSize(label) {
    var match = String(label || '').match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i);
    if (!match) return null;
    var w = parseFloat(match[1].replace(',', '.'));
    var h = parseFloat(match[2].replace(',', '.'));
    var cmKey = Math.round(w) + 'x' + Math.round(h);
    // US and CA see the value translated to inches per market ("12 × 16 in",
    // French "12 × 16 po": tools/set-size-inches.mjs).
    var isInch = !CM_TO_IN[cmKey] && /(″|"|\bin\b|\bpo\b|inch)/i.test(label);
    return {
      cm: isInch ? [w * IN, h * IN] : [w, h],
      inchKey: isInch ? cmKey : CM_TO_IN[cmKey] || null
    };
  }

  class FramePreview extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.update(false);

      // Capture phase: the drawer stops the radio's change while it is
      // portalled (assets/option-drawer.js) and product-info-bento.js does not
      // let it bubble to the document either. The drawer resets the
      // passe-partout and moves home synchronously, so once the dispatch is
      // over (a task, not a frame: rAF waits for the next paint) the picker's
      // radios hold the new selection.
      this.onChange = function (event) {
        var input = event.target;
        if (!input || input.type !== 'radio' || !self.isOwnRadio(input)) return;
        setTimeout(function () {
          self.update(true);
        }, 0);
      };
      document.addEventListener('change', this.onChange, true);

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(function () {
          self.update(false);
        });
        this.resizeObserver.observe(this);
      }

      var events = window.LeonartoEvents;
      var names = window.LeonartoTheme && window.LeonartoTheme.pubSubEvents;
      if (!events || !names || this.unsubscribe) return;
      this.unsubscribe = events.subscribe(names.variantChange, function (event) {
        var data = (event && event.data) || {};
        if (data.sectionId && data.sectionId !== self.dataset.sectionId) return;
        self.update(false);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onChange, true);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.unsubscribe) this.unsubscribe();
      this.unsubscribe = null;
    }

    picker() {
      return document.getElementById('variant-picker-' + this.dataset.sectionId);
    }

    // A radio of one of the three options (it may sit in a drawer portalled
    // to <body>, outside the picker).
    isOwnRadio(input) {
      var config = this.config();
      if (!config) return false;
      var name = input.name.trim();
      return name === config.frameInput || name === config.sizeInput || name === config.matInput;
    }

    config() {
      var picker = this.picker();
      var node = picker && picker.querySelector('[data-frame-state]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return null;
      }
    }

    // Compared trimmed: product-variant-options renders some names with a
    // trailing newline.
    radios(picker, name) {
      if (!name) return [];
      return Array.prototype.filter.call(picker.querySelectorAll('input[type="radio"]'), function (input) {
        return input.name.trim() === name;
      });
    }

    checked(picker, name) {
      return (
        this.radios(picker, name).filter(function (input) {
          return input.checked;
        })[0] || null
      );
    }

    readState() {
      var picker = this.picker();
      var config = this.config();
      if (!config || !config.allowed) return null;

      var frame = this.checked(picker, config.frameInput);
      if (!frame || !frame.dataset.frameKey) return null;
      var material = frame.dataset.frameMaterial || 'wood';

      var sizeRadio = this.checked(picker, config.sizeInput);
      var size = parseSize(sizeRadio ? sizeRadio.value : '');
      if (!size) return null;

      var mat = false;
      if (material === 'wood' && config.matInput) {
        var group = this.radios(picker, config.matInput);
        mat = group.length > 1 && !group[0].checked && !!this.checked(picker, config.matInput);
      }

      return {
        key: frame.dataset.frameKey,
        label: frame.value,
        material: material,
        size: size,
        mat: mat
      };
    }

    // The size labels are portrait ("30x40 cm") on every product; a landscape
    // print shows in the same frame turned, which the artwork's proportions
    // tell (image_tag writes its width and height). Within 3% of 1:1 it is
    // square: the frame then follows the label as written ("30x30 cm").
    shape() {
      var art = this.querySelector('.lt-frame-preview__art');
      if (!art) return 'portrait';
      var w = Number(art.getAttribute('width')) || art.naturalWidth;
      var h = Number(art.getAttribute('height')) || art.naturalHeight;
      if (!w || !h) return 'portrait';
      if (Math.max(w, h) / Math.min(w, h) < 1.03) return 'square';
      return w > h ? 'landscape' : 'portrait';
    }

    update(fromChange) {
      var state = this.readState();
      this.hidden = !state;
      if (!state) return;
      var shape = this.shape();
      var landscape = shape === 'landscape';
      // A pair of sides laid the way the artwork is: long side across for
      // landscape, up for portrait, as written for square.
      var turn = function (pair) {
        if (shape === 'square') return pair;
        var long = Math.max(pair[0], pair[1]);
        var short = Math.min(pair[0], pair[1]);
        return landscape ? [long, short] : [short, long];
      };

      // Everything in inches: the frame's inner edge (wood: the Artelo inch
      // frame; canvas and unframed: the print in cm), the window inset, the
      // outer edge.
      var inner = state.size.cm.map(function (cm) {
        return cm / IN;
      });
      if (state.material === 'wood' && state.size.inchKey) {
        inner = state.size.inchKey.split('x').map(Number);
      }
      inner = turn(inner);
      var win = WINDOW_IN[state.material] || 0;
      var outerW = inner[0] + 2 * win;
      var outerH = inner[1] + 2 * win;

      // The scale follows the long side, so a size reads the same in both
      // orientations; a landscape piece is then held back by the width.
      var outerLong = Math.max(outerW, outerH);
      var t = (outerLong * IN - SCALE.minCm) / (SCALE.maxCm - SCALE.minCm);
      var heightPct = SCALE.minPct + Math.max(0, Math.min(1, t)) * (SCALE.maxPct - SCALE.minPct);
      var ratio = this.clientHeight ? this.clientWidth / this.clientHeight : 1;
      var k = Math.min(heightPct / outerLong, (SCALE.widthPct * ratio) / outerW); // cqh per inch

      var len = function (inches) {
        return (inches * k).toFixed(3) + 'cqh';
      };
      var lens = function (list) {
        return list.map(len).join(' ');
      };

      // Passe-partout: margins of this frame size; the mat render's slices
      // scale by this size's margin over the 18x24 render's.
      var opening = state.mat && state.size.inchKey && MAT_WINDOW[state.size.inchKey];
      if (opening) opening = turn(opening);
      var matRef = MAT_REF_IN[landscape ? 'landscape' : 'portrait'];
      var matX = opening ? (inner[0] - opening[0]) / 2 : 0;
      var matY = opening ? (inner[1] - opening[1]) / 2 : 0;
      var fx = opening ? matX / matRef[0] : 1;
      var fy = opening ? matY / matRef[1] : 1;
      var mat = landscape ? SLICE_IN.matLandscape : SLICE_IN.mat;

      var props = {
        '--lt-w': len(outerW),
        '--lt-h': len(outerH),
        '--lt-win': len(win),
        '--lt-art-t': len(matY),
        '--lt-art-r': len(matX),
        '--lt-art-b': len(matY),
        '--lt-art-l': len(matX),
        '--lt-mat': lens([mat[0] * fy, mat[1] * fx, mat[2] * fy, mat[3] * fx]),
        '--lt-frame-wood': lens(SLICE_IN.frameWood),
        '--lt-frame-canvas': lens(SLICE_IN.frameCanvas),
        '--lt-shadow-wood': lens(SLICE_IN.shadowWood),
        '--lt-shadow-canvas': lens(SLICE_IN.shadowCanvas),
        '--lt-paper': lens(SLICE_IN.paper),
        '--lt-weave': len(WEAVE_IN)
      };
      for (var name in props) this.style.setProperty(name, props[name]);
      // Fit instead of crop inside the passe-partout opening (lt-frame-preview.css).
      this.classList.toggle('has-mat', !!opening);
      // The landscape passe-partout render and its slices (lt-frame-preview.css).
      this.classList.toggle('is-landscape', landscape);

      var on = {
        'shadow-wood': state.material === 'wood',
        'shadow-canvas': state.material === 'canvas',
        paper: state.material === 'none',
        weave: state.material === 'canvas',
        mat: !!opening
      };
      on['frame-' + state.key] = true;
      Array.prototype.forEach.call(this.querySelectorAll('[data-layer]'), function (layer) {
        layer.classList.toggle('is-on', !!on[layer.dataset.layer]);
      });

      // The thumbnail copy is decorative and has no template.
      var template = this.dataset.altTemplate;
      if (template) this.setAttribute('aria-label', template.replace('%frame%', state.label));

      // First draw: commit the starting sizes (reading offsetWidth flushes
      // style), then switch transitions on for every later change.
      if (!this.classList.contains('is-ready')) {
        void this.offsetWidth;
        this.classList.add('is-ready');
      }

      // An option was just picked: bring the slide that shows it into view
      // (the front shot's, second since 2026-11-03; the room mock-up leads).
      if (fromChange) {
        var gallery = this.closest('m-pdp-gallery');
        var slide = this.closest('[data-index]');
        if (gallery && slide && typeof gallery.goTo === 'function') gallery.goTo(Number(slide.dataset.index));
      }
    }
  }

  customElements.define('lt-frame-preview', FramePreview);
})();
